import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQueryClient } from "react-query";
import { EmployerODPResponse, Limit } from "../@types/blockedDates";
import useUpdateEmployerODPLimitConfigApiService from "../services/api/UpdateEmployerODPLimitConfigApiService";
import { useToasts } from "../../../providers/notifications/ToastsContext";

interface LimitsProviderProps {
  children: React.ReactNode;
  initialConfig?: EmployerODPResponse;
}

interface CompanyLimitsContextType {
  companyLimits: EmployerODPResponse | null;
  updateCompanyLimits: (
    limitType:
      | "standard"
      | "instant"
      | "volume_and_value"
      | "value_added_service",
    limitKey: "amount_limit" | "request_limit",
    newValue: number,
    newEnabled: boolean,
    intent: "enable" | "disable" | "edit"
  ) => void;
  isLoading: boolean;
}

const CompanyLimitsContext =
  React.createContext<CompanyLimitsContextType | null>(null);

const CompanyLimitsProvider = ({
  children,
  initialConfig,
}: LimitsProviderProps): JSX.Element => {
  const [companyLimits, setCompanyLimits] =
    useState<EmployerODPResponse | null>(initialConfig || null);
  const queryClient = useQueryClient();
  const {
    isSuccess: isUpdateSuccess,
    mutate: onUpdate,
    isLoading,
  } = useUpdateEmployerODPLimitConfigApiService();
  const { showToast } = useToasts();

  useEffect(() => {
    if (initialConfig) {
      setCompanyLimits(initialConfig);
    }
  }, [initialConfig]);

  useEffect(() => {
    // Check for intent in session storage. we do this to show a toast after page reload
    const storedIntent = sessionStorage.getItem("updateIntent");
    if (storedIntent) {
      switch (storedIntent) {
        case "edit":
          showToast({ strapiId: "company_ewa_limits_edited" });
          break;
        case "enable":
          showToast({ strapiId: "company_ewa_limits_enabled" });
          break;
        case "disable":
          showToast({ strapiId: "company_ewa_limits_disabled" });
          break;
        default:
          break;
      }
      // Clear the stored intent after processing
      sessionStorage.removeItem("updateIntent");
    }
  }, []);

  const updateCompanyLimits = useCallback(
    (
      limitType:
        | "standard"
        | "instant"
        | "volume_and_value"
        | "value_added_service",
      limitKey: "amount_limit" | "request_limit",
      newValue: number,
      newEnabled: boolean,
      intent: "enable" | "disable" | "edit"
    ) => {
      if (!companyLimits) return;

      const defaultLimit: Limit = {
        enabled: false,
        time_period: "RECON",
        value: 0,
      };

      const newLimits = {
        ...companyLimits,
        [limitType]: {
          ...companyLimits[limitType],
          [limitKey]: {
            ...(companyLimits[limitType]?.[limitKey] || defaultLimit),
            value: newValue,
            enabled: newEnabled,
          },
        },
      };

      setCompanyLimits(newLimits);
      onUpdate({ config: newLimits });

      // Store intent in session storage
      sessionStorage.setItem("updateIntent", intent);

      // Force reload
      window.location.reload();
    },
    [companyLimits, onUpdate, queryClient, showToast]
  );

  useEffect(() => {
    if (isUpdateSuccess) {
      queryClient.invalidateQueries(["GetEmployerODPLimitsApiService"]);
    }
  }, [isUpdateSuccess]);

  const value = useMemo(
    () => ({
      companyLimits,
      updateCompanyLimits,
      isLoading,
    }),
    [companyLimits, updateCompanyLimits, isLoading]
  );

  return (
    <CompanyLimitsContext.Provider value={value}>
      {children}
    </CompanyLimitsContext.Provider>
  );
};

export default CompanyLimitsProvider;

export const useCompanyLimits = (): CompanyLimitsContextType => {
  const context = useContext(CompanyLimitsContext);
  if (context === null) {
    throw new Error(
      "useCompanyLimits must be used within a CompanyLimitsProvider"
    );
  }
  return context;
};
